import React, { useEffect, useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { DialogFooter, ErrorBox, FieldError, NoteField, RowContext } from './dialog_parts';
import { rupees, waive_pass_option } from './adapters';
import { waive_form_errors } from './form_errors';
import { reason_options } from './params';

/*
 * waive or un-waive one penalty: a full waive of the row's current charge, its own request and its
 * own audit row. Partial relief is a correction.
 */
// load_pass: () => Promise<free_pass_view> for the row's teacher-month; on_use_pass(view) hands over
// to the Free Pass dialog. Without them the dialog is a plain waive.
const WaiveForm = ({
  row,
  waive,
  saving,
  error,
  on_submit,
  on_close,
  on_focus_lost,
  load_pass = null,
  on_use_pass = null
}) => {
  const [form, setForm] = useState({ reason_code: '', note: '', approved_by: '' });
  const offers_pass = waive && !!load_pass && !!on_use_pass;
  const [pass, setPass] = useState({ loading: true, view: null, error_text: '' });
  useEffect(() => {
    if (!offers_pass) return undefined;
    let cancelled = false;
    setPass({ loading: true, view: null, error_text: '' });
    load_pass()
      .then((view) => {
        if (!cancelled) setPass({ loading: false, view, error_text: '' });
      })
      .catch((e) => {
        if (cancelled) return;
        setPass({
          loading: false,
          view: null,
          error_text: (e && e.message) || 'Free Pass Days could not be loaded.'
        });
      });
    return () => {
      cancelled = true;
    };
    // load_pass is rebuilt on every parent render; the row identifies the teacher-month.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers_pass, row.uuid]);
  const pass_option = offers_pass ? waive_pass_option(row, pass) : null;
  const [touched, setTouched] = useState(false);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const errors = waive_form_errors(form, waive);
  const server = (error && error.fields) || {};
  const title = waive ? 'Waive penalty' : 'Undo the waiver';

  const submit = () => {
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    on_submit({ ...form, waive });
  };

  return (
    <TpDialog
      title_id="tp-pe-waive-title"
      title={title}
      size="md"
      busy={saving}
      guard_text={discard_guard_text(form.note, form.approved_by)}
      on_close={on_close}
      on_focus_lost={on_focus_lost}
    >
      <div className="tp-modal">
        <RowContext row={row} />
        <div className="tp-hint">
          {waive
            ? `Waiving removes the whole ${rupees(row.charged)} charge from ${row.teacher_name}'s month. To reduce it instead, use Correct.`
            : `The ${rupees(row.basis)} charge goes back on ${row.teacher_name}'s month. The penalty stays as Ops set it; use Revert to hand it back to the system.`}
        </div>
        {pass_option ? (
          <div className="tp-field" data-testid="tp-pe-waive-pass">
            <span className="tp-card-field-label">Or use a Free Pass Day</span>
            <span className="tp-hint" id="tp-pe-waive-pass-hint">
              {pass_option.text}
            </span>
            <div>
              <button
                type="button"
                className="tp-btn-out"
                disabled={!pass_option.enabled || saving}
                aria-describedby="tp-pe-waive-pass-hint"
                onClick={() => on_use_pass(pass.view)}
              >
                Use a Free Pass Day instead
              </button>
            </div>
          </div>
        ) : null}
        <div className="tp-field">
          <label htmlFor="tp-pe-waive-reason">Reason</label>
          <select
            id="tp-pe-waive-reason"
            className="tp-select"
            value={form.reason_code}
            onChange={(e) => set('reason_code', e.target.value)}
          >
            <option value="">Choose a reason</option>
            {reason_options('waive').map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <FieldError text={(touched && errors.reason_code) || server.reason_code} />
        </div>
        {waive ? (
          <div className="tp-field">
            <label htmlFor="tp-pe-waive-approved">Approved by (optional)</label>
            <input
              id="tp-pe-waive-approved"
              className="tp-input"
              value={form.approved_by}
              placeholder="For example, Priya (Ops lead)"
              onChange={(e) => set('approved_by', e.target.value)}
            />
            <FieldError text={(touched && errors.approved_by) || server.approved_by} />
          </div>
        ) : null}
        <NoteField
          id="tp-pe-waive-note"
          label="Note (internal, required)"
          value={form.note}
          on_change={(v) => set('note', v)}
          error_text={(touched && errors.note) || server.note}
          placeholder={waive ? 'Why the penalty is waived' : 'Why the waiver is undone'}
        />
        <ErrorBox error={error} />
        <DialogFooter
          saving={saving}
          error={error}
          submit_label={waive ? 'Waive penalty' : 'Undo waiver'}
          on_submit={submit}
          on_confirm={submit}
          on_close={on_close}
        />
      </div>
    </TpDialog>
  );
};

export default WaiveForm;
